import { cn } from "@/lib/utils";
import { useState, useEffect, useRef, forwardRef } from "react";
import mascotNormal from "@/assets/mascot-normal.png";
import mascotHappy from "@/assets/mascot-happy.png";
import mascotSad from "@/assets/mascot-sad.png";

export type MascotMood = 
  | "happy" 
  | "thinking" 
  | "confused" 
  | "relaxed" 
  | "excited" 
  | "sad" 
  | "waving";

export type MascotSize = "xs" | "sm" | "logo" | "md" | "lg" | "xl";

interface MascotProps {
  mood?: MascotMood;
  size?: MascotSize;
  className?: string;
  animate?: boolean;
  /** Render mascot in white (for dark backgrounds like the Check In button) */
  inverted?: boolean;
  /** Boost image priority for above-the-fold hero usage */
  priority?: boolean;
}

const sizeMap: Record<MascotSize, { className: string; width: number; height: number }> = {
  xs: { className: "h-6 w-6", width: 24, height: 24 },
  sm: { className: "h-9 w-9", width: 36, height: 36 },
  logo: { className: "h-11 w-11", width: 44, height: 44 },
  md: { className: "h-16 w-16", width: 64, height: 64 },
  lg: { className: "h-[120px] w-[120px]", width: 120, height: 120 },
  xl: { className: "h-40 w-40", width: 160, height: 160 },
};

// Map moods to the appropriate mascot image
const emotionImages: Record<MascotMood, string> = {
  happy: mascotHappy,
  thinking: mascotNormal,
  relaxed: mascotNormal,
  waving: mascotHappy,
  excited: mascotHappy,
  sad: mascotSad,
  confused: mascotSad,
};

// Normalize a Vite import path to an absolute URL for comparison
const toAbsoluteUrl = (src: string) => {
  try {
    return new URL(src, window.location.origin).href;
  } catch {
    return src;
  }
};

// Warm all three moods so switching mood never flashes.
//
// This used to run on MODULE LOAD, which meant every page that merely had this
// module in its bundle downloaded all three mascots — including the public
// landing page, which renders no <Mascot> at all and was pulling ~1.7MB of
// unused image on every visit. Warming on first mount instead keeps the
// no-flash behaviour for pages that actually use a Mascot, and costs pages that
// do not exactly nothing.
const preloadedImages: HTMLImageElement[] = [];
let warmed = false;
function warmMascotImages() {
  if (warmed || typeof window === "undefined") return;
  warmed = true;
  [mascotNormal, mascotHappy, mascotSad].forEach((src) => {
    const img = new Image();
    img.src = src;
    preloadedImages.push(img);
  });
}

// ForwardRef wrapper to support Radix UI dialog ref forwarding
export const Mascot = forwardRef<HTMLDivElement, MascotProps>(function Mascot({ 
  mood = "happy", 
  size = "md", 
  className,
  animate = false,
  inverted = false,
  priority = false,
}, ref) {
  warmMascotImages();
  const imageSrc = emotionImages[mood];
  const imgRef = useRef<HTMLImageElement>(null);
  const sizeConfig = sizeMap[size];
  
  const normalizedSrc = toAbsoluteUrl(imageSrc);
  const isPreloaded = preloadedImages.some(img => img.src === normalizedSrc && img.complete && img.naturalWidth > 0);
  const [imageLoaded, setImageLoaded] = useState(isPreloaded);

  useEffect(() => {
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) {
      setImageLoaded(true);
    }
  }, [imageSrc]);

  return (
    <div 
      ref={ref}
      className={cn(
        "relative inline-flex items-center justify-center",
        sizeConfig.className,
        animate && "animate-gentle-bounce",
        className
      )}
    >
      <img
        ref={imgRef}
        src={imageSrc}
        alt={`Grounded mascot - ${mood}`}
        width={sizeConfig.width}
        height={sizeConfig.height}
        fetchPriority={priority ? "high" : undefined}
        className={cn(
          "h-full w-full object-contain",
          !imageLoaded && "hidden",
          !inverted && "mix-blend-multiply"
        )}
        style={inverted ? {
          filter: "drop-shadow(0 0 1px rgba(255,255,255,0.5)) drop-shadow(0 0 1.5px rgba(255,255,255,0.4))"
        } : undefined}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageLoaded(false)}
      />
    </div>
  );
});
